import React, { useState, useEffect } from 'react';
import { X, Save, User, MapPin, FileText, Award, Clock, UserPlus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NotaryFormModal = ({ notary, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userProfiles, setUserProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [creationMode, setCreationMode] = useState('direct');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      user_id: notary?.user_id || '',
      notary_name: notary?.notary_name || '',
      license_number: notary?.license_number || '',
      office_address: notary?.office_address || '',
      specializations: notary?.specializations?.join(', ') || '',
      is_verified: notary?.is_verified || false,
      available_hours: JSON.stringify(notary?.available_hours || {})
    }
  });

  useEffect(() => {
    loadUserProfiles();
  }, []);

  useEffect(() => {
    if (notary) {
      setValue('user_id', notary?.user_id || '');
      setValue('notary_name', notary?.notary_name || '');
      setValue('license_number', notary?.license_number);
      setValue('office_address', notary?.office_address);
      setValue('specializations', notary?.specializations?.join(', ') || '');
      setValue('is_verified', notary?.is_verified);
      setValue('available_hours', JSON.stringify(notary?.available_hours || {}));
      setCreationMode(notary?.user_id ? 'profile' : 'direct');
    }
  }, [notary, setValue]);

  const loadUserProfiles = async () => {
    try {
      setLoadingProfiles(true);
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role')
        ?.order('full_name');
      if (error) throw error;
      const existingNotaryUsers = [];
      if (!notary?.id) {
        const { data: existingNotaries } = await supabase
          ?.from('notaries')
          ?.select('user_id')
          ?.not('user_id', 'is', null);
        if (existingNotaries) {
          existingNotaryUsers?.push(...existingNotaries?.map(n => n?.user_id)?.filter(id => id));
        }
      }
      const availableProfiles = (data || [])?.filter(profile =>
        profile?.id && (!existingNotaryUsers?.includes(profile?.id) || profile?.id === notary?.user_id)
      );
      setUserProfiles(availableProfiles);
    } catch (error) {
      console.error('Error loading user profiles:', error);
      setError('Erreur lors du chargement des profils utilisateur');
    } finally {
      setLoadingProfiles(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      setError('');
      if (creationMode === 'profile') {
        if (!formData?.user_id || formData?.user_id === '' || formData?.user_id === 'null') {
          setError('Vous devez sélectionner un profil utilisateur en mode liaison');
          return;
        }
      } else {
        if (!formData?.notary_name?.trim()) {
          setError('Le nom du notaire est requis en mode direct');
          return;
        }
      }
      const specializations = formData?.specializations
        ?.split(',')
        ?.map(s => s?.trim())
        ?.filter(s => s?.length > 0) || [];
      let availableHours = {};
      try {
        if (formData?.available_hours?.trim()) {
          availableHours = JSON.parse(formData?.available_hours);
        }
      } catch (e) {
        setError('Format JSON invalide pour les horaires disponibles');
        return;
      }
      const notaryData = {
        user_id: creationMode === 'profile' && formData?.user_id ? formData?.user_id : null,
        notary_name: formData?.notary_name?.trim() || null,
        license_number: formData?.license_number?.trim(),
        office_address: formData?.office_address?.trim(),
        specializations,
        is_verified: formData?.is_verified,
        available_hours: availableHours
      };
      if (notary?.id) {
        const { error } = await supabase?.from('notaries')?.update(notaryData)?.eq('id', notary?.id);
        if (error) throw error;
      } else {
        const { error } = await supabase?.from('notaries')?.insert([notaryData]);
        if (error) throw error;
      }
      onSuccess?.();
    } catch (error) {
      console.error('Error saving notary:', error);
      if (error?.code === '23505') {
        setError('Ce numéro de licence est déjà utilisé par un autre notaire');
      } else if (error?.code === '22P02') {
        setError('Erreur: identifiant utilisateur invalide. Veuillez sélectionner un utilisateur valide.');
      } else {
        setError(error?.message || 'Erreur lors de la sauvegarde du notaire');
      }
    } finally {
      setLoading(false);
    }
  };

  const defaultAvailableHours = {
    "monday": { "start": "09:00", "end": "17:00" },
    "tuesday": { "start": "09:00", "end": "17:00" },
    "wednesday": { "start": "09:00", "end": "17:00" },
    "thursday": { "start": "09:00", "end": "17:00" },
    "friday": { "start": "09:00", "end": "17:00" },
    "saturday": { "start": "09:00", "end": "12:00" },
    "sunday": { "closed": true }
  };

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border border-border w-full max-w-2xl shadow-lg rounded-lg bg-card">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            {notary ? 'Modifier le Notaire' : 'Ajouter un Notaire'}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Creation Mode Selection */}
        {!notary?.id && (
          <div className="mb-6 p-4 bg-muted/40 border border-border rounded-lg">
            <h4 className="text-sm font-medium text-foreground mb-3">Mode de création</h4>
            <div className="flex space-x-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="creationMode"
                  value="direct"
                  checked={creationMode === 'direct'}
                  onChange={(e) => setCreationMode(e?.target?.value)}
                  className="h-4 w-4 border-border text-brand-gold focus:ring-brand-gold/30"
                />
                <span className="ml-2 text-sm text-foreground flex items-center">
                  <UserPlus className="w-4 h-4 mr-1 text-muted-foreground" />
                  Création directe
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="creationMode"
                  value="profile"
                  checked={creationMode === 'profile'}
                  onChange={(e) => setCreationMode(e?.target?.value)}
                  className="h-4 w-4 border-border text-brand-gold focus:ring-brand-gold/30"
                />
                <span className="ml-2 text-sm text-foreground flex items-center">
                  <User className="w-4 h-4 mr-1 text-muted-foreground" />
                  Liaison profil utilisateur
                </span>
              </label>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {creationMode === 'direct' ?'Créez un notaire avec juste son nom, sans liaison à un compte utilisateur.' :'Associez ce notaire à un profil utilisateur existant pour lui donner accès au système.'
              }
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* User Profile Selection */}
          {creationMode === 'profile' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                <User className="w-4 h-4 inline mr-1.5 text-muted-foreground" />
                Profil Utilisateur *
              </label>
              {loadingProfiles ? (
                <div className="text-sm text-muted-foreground py-2">Chargement des profils...</div>
              ) : userProfiles?.length === 0 ? (
                <div className="text-sm text-yellow-700 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  Aucun utilisateur disponible pour liaison.
                  <br />
                  <span className="text-xs text-muted-foreground mt-1 block">
                    Utilisez le mode "Création directe" ou créez d'abord un profil utilisateur.
                  </span>
                </div>
              ) : (
                <select
                  {...register('user_id', creationMode === 'profile' ? {
                    required: 'Le profil utilisateur est requis en mode liaison',
                    validate: value => (value && value !== '' && value !== 'null') || 'Veuillez sélectionner un utilisateur valide'
                  } : {})}
                  className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold"
                >
                  <option value="">Sélectionnez un profil utilisateur</option>
                  {userProfiles?.map((profile) => (
                    <option key={profile?.id} value={profile?.id}>
                      {profile?.full_name} ({profile?.email}) - {profile?.role}
                    </option>
                  ))}
                </select>
              )}
              {errors?.user_id && (
                <p className="mt-1 text-xs text-destructive">{errors?.user_id?.message}</p>
              )}
            </div>
          )}

          {/* Notary Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              <User className="w-4 h-4 inline mr-1.5 text-muted-foreground" />
              Nom du Notaire {creationMode === 'direct' && '*'}
            </label>
            <Input
              type="text"
              {...register('notary_name', creationMode === 'direct' ? {
                required: 'Le nom du notaire est requis',
                minLength: { value: 2, message: 'Le nom doit faire au moins 2 caractères' }
              } : {})}
              placeholder="Saisissez le nom complet du notaire"
            />
            {errors?.notary_name && (
              <p className="mt-1 text-xs text-destructive">{errors?.notary_name?.message}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              {creationMode === 'direct'
                ? "Nom complet tel qu'il apparaîtra dans les documents officiels"
                : 'Nom optionnel pour remplacer le nom du profil utilisateur'
              }
            </p>
          </div>

          {/* License Number */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              <FileText className="w-4 h-4 inline mr-1.5 text-muted-foreground" />
              Numéro de Licence *
            </label>
            <Input
              type="text"
              {...register('license_number', {
                required: 'Le numéro de licence est requis',
                minLength: { value: 5, message: 'Le numéro de licence doit faire au moins 5 caractères' }
              })}
              placeholder="Ex: Arrété 178/MJDH/DSJRH du 07 JUIN 2019"
            />
            {errors?.license_number && (
              <p className="mt-1 text-xs text-destructive">{errors?.license_number?.message}</p>
            )}
          </div>

          {/* Office Address */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1.5 text-muted-foreground" />
              Adresse du Bureau *
            </label>
            <textarea
              {...register('office_address', {
                required: "L'adresse du bureau est requise",
                minLength: { value: 10, message: "L'adresse doit faire au moins 10 caractères" }
              })}
              rows={3}
              className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold resize-none"
              placeholder="Adresse complète du bureau notarial"
            />
            {errors?.office_address && (
              <p className="mt-1 text-xs text-destructive">{errors?.office_address?.message}</p>
            )}
          </div>

          {/* Specializations */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              <Award className="w-4 h-4 inline mr-1.5 text-muted-foreground" />
              Spécialisations
            </label>
            <Input
              type="text"
              {...register('specializations')}
              placeholder="Séparez les spécialisations par des virgules (ex: Droit immobilier, Succession)"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Séparez chaque spécialisation par une virgule
            </p>
          </div>

          {/* Available Hours */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              <Clock className="w-4 h-4 inline mr-1.5 text-muted-foreground" />
              Horaires Disponibles (JSON)
            </label>
            <textarea
              {...register('available_hours')}
              rows={8}
              className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold resize-none"
              placeholder={JSON.stringify(defaultAvailableHours, null, 2)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Format JSON des horaires de disponibilité. Laissez vide pour les horaires par défaut.
            </p>
          </div>

          {/* Verification Status */}
          <div className="flex items-center space-x-2 p-3 bg-muted/40 border border-border rounded-md">
            <input
              type="checkbox"
              {...register('is_verified')}
              id="is_verified"
              className="h-4 w-4 rounded border-border text-brand-gold focus:ring-brand-gold/30"
            />
            <label htmlFor="is_verified" className="text-sm text-foreground cursor-pointer">
              Notaire vérifié et approuvé
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" variant="elegant" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Sauvegarde...' : notary ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotaryFormModal;